// SETTINGS V2 — Team desktop/tablet sortable header row (SETTINGS_V2_SP6 §5.2).
//
// Grid header mirroring the designer TeamHead: TEAM_GRID columns, 6 reset-styled
// sort buttons each with an inline SSortGlyph (two chevrons; active direction in
// var(--mod-accent), inactive var(--faint) — ported from designer settings-kit
// SSortGlyph, plain SVG, no icon lib) + a 7th empty actions cell. Click dispatches
// the legacy toggle semantics (same column → flip desc; new column → asc) via the
// page's onSort. aria-sort on the active columnheader. Inline styles + scoped
// CSS-var tokens; no MUI/sx/dt/Tailwind. SEC-REQ-2: no import-time side effects.
import React from "react";
import type { TFunction } from "i18next";
import { EMPLOYEE_SORT_PARAMETERS } from "../../models/userModels";

/** Shared grid template — designer TEAM_GRID (settings-pages-b line 144). Also
 *  consumed by TeamRowV2 so the row cells align under the header cells. */
export const TEAM_GRID =
  "minmax(220px, 1.3fr) 120px 130px 130px 150px 100px 36px";

// Two stacked chevrons; the active sort direction paints in the accent, the
// other stays faint (designer SSortGlyph).
const SSortGlyph: React.FC<{ active: boolean; desc: boolean }> = ({
  active,
  desc,
}) => {
  const up = active && !desc ? "var(--mod-accent)" : "var(--faint)";
  const down = active && desc ? "var(--mod-accent)" : "var(--faint)";
  return (
    <svg
      width="7"
      height="11"
      viewBox="0 0 7 11"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M3.5 0L6.5 3.5H0.5L3.5 0Z" fill={up} />
      <path d="M3.5 11L0.5 7.5H6.5L3.5 11Z" fill={down} />
    </svg>
  );
};

export interface TeamHeadV2Props {
  sortBy?: number;
  sortDesc: boolean;
  onSort: (parameter: number) => void;
  t: TFunction;
}

const TeamHeadV2: React.FC<TeamHeadV2Props> = ({
  sortBy,
  sortDesc,
  onSort,
  t,
}) => {
  const cell = (labelKey: string, parameter: number) => {
    const active = sortBy === parameter;
    return (
      <div
        role="columnheader"
        aria-sort={active ? (sortDesc ? "descending" : "ascending") : "none"}
        style={{ minWidth: 0 }}
      >
        <button
          type="button"
          onClick={() => onSort(parameter)}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: active ? "var(--mod-accent)" : "var(--faint)",
            maxWidth: "100%",
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(labelKey) as string}
          </span>
          <SSortGlyph active={active} desc={sortDesc} />
        </button>
      </div>
    );
  };

  return (
    <div
      role="row"
      style={{
        display: "grid",
        gridTemplateColumns: TEAM_GRID,
        gap: 12,
        padding: "8px 16px",
        alignItems: "center",
        borderBottom: "1px solid var(--border)",
      }}
    >
      {cell("settings:employee", EMPLOYEE_SORT_PARAMETERS.EMPLOYEE)}
      {cell("settings:department", EMPLOYEE_SORT_PARAMETERS.DEPARTMENT)}
      {cell("settings:access", EMPLOYEE_SORT_PARAMETERS.ACCESS)}
      {cell("settings:joined", EMPLOYEE_SORT_PARAMETERS.JOINED)}
      {cell("settings:validUntil", EMPLOYEE_SORT_PARAMETERS.VALID_UNTIL)}
      {cell("settings:status", EMPLOYEE_SORT_PARAMETERS.STATUS)}
      {/* 7th cell: actions column — empty, labelled for AT only (SP4 kebab parity). */}
      <div role="columnheader" aria-label={t("settings:actions") as string} />
    </div>
  );
};

export default TeamHeadV2;
